import  { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import styled from "styled-components";
import Button from "react-bootstrap/Button";
import { BsGithub, BsArrowLeft, BsCalendar, BsPersonWorkspace } from "react-icons/bs";
import { CgWebsite } from "react-icons/cg";
import { FiChevronRight } from "react-icons/fi";
import Accordion from "react-bootstrap/Accordion";
import LivePreviewButton from "../livePreview";
import { MdOpenInNew } from "react-icons/md";

const PageWrapper = styled.div`
  min-height: 100vh;
  background: var(--bg);
  padding: 100px 20px 60px;
  
  @media (max-width: 768px) {
    padding: 80px 16px 40px;
  }
`;

const Content = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1.4fr 0.6fr;
  gap: 48px;
  position: relative;

  @media (max-width: 992px) {
    grid-template-columns: 1fr;
    gap: 32px;
  }
`;

const MainContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

const Sidebar = styled.div`
  position: sticky;
  top: 100px;
  height: fit-content;
  
  @media (max-width: 992px) {
    position: static;
  }
`;

const ProjectHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 8px;

  .back-btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    color: var(--text-secondary);
    background: transparent;
    border: 1px solid var(--card-border);
    border-radius: 12px;
    padding: 8px;
  font-size: 0.95rem;
    transition: all 0.3s;
    cursor: pointer;
    width: fit-content;

    &:hover {
      color: var(--primary-color);
      transform: translateX(-4px);
    }
  }

  .title {
    font-size: 2.8rem;
    font-weight: 800;
    line-height: 1.2;
    color: var(--text-primary);
    margin: 0;
    background: linear-gradient(135deg, var(--primary-color), var(--secondary-color));
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    text-align: start;
    display: inline-block;


    @media (max-width: 768px) {
      font-size: 2.2rem;
    }
  }

  .subtitle {
    font-size: 1.1rem;
    color: var(--text-secondary);
    font-weight: 500;
    margin-bottom: 4px;
  }
`;

const MetaInfo = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  margin: 16px 0;

  .meta-item {
    display: flex;
    align-items: center;
    gap: 8px;
    color: var(--muted);
    font-size: 0.95rem;

    svg {
      color: var(--primary-color);
      font-size: 1.1rem;
    }
  }
`;

const ImageGallery = styled.div`
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  border: 1px solid var(--card-border);
  background: var(--card-bg);

  .main-image {
    width: 100%;
    object-fit: cover;
    display: block;
    transition: transform 0.5s;

    &:hover {
      transform: scale(1.02);
    }
    @media (max-width: 768px) {
      height: 300px;
    }
  }

  .thumbnails {
    display: flex;
    gap: 12px;
    padding: 20px;
    background: rgba(0, 0, 0, 0.2);
    border-top: 1px solid rgba(255, 255, 255, 0.1);
    overflow-x: auto;

    &::-webkit-scrollbar {
      height: 4px;
    }

    &::-webkit-scrollbar-thumb {
      background: var(--primary-color);
      border-radius: 4px;
    }
  }

  .thumbnail {
    width: 80px;
    height: 60px;
    object-fit: cover;
    border-radius: 8px;
    cursor: pointer;
    opacity: 0.6;
    transition: all 0.3s;
    border: 2px solid transparent;
    flex-shrink: 0;

    &:hover {
      opacity: 1;
      transform: translateY(-2px);
    }

    &.active {
      opacity: 1;
      border-color: var(--primary-color);
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(var(--primary-color-rgb), 0.3);
    }
  }
`;

const ContentSection = styled.div`
  background: var(--card-bg);
  border: 1px solid var(--card-border);
  border-radius: 16px;
  padding: 32px;
  backdrop-filter: blur(10px);

  @media (max-width: 768px) {
    padding: 24px;
  }

  .section-title {
    display: flex;
    align-items: center;
    gap: 12px;
    font-size: 1.4rem;
    font-weight: 700;
    color: var(--text-primary);
    margin-bottom: 20px;

    &::before {
      content: '';
      display: block;
      width: 4px;
      height: 24px;
      background: linear-gradient(to bottom, var(--primary-color), var(--secondary-color));
      border-radius: 2px;
    }
  }

  .section-content {
    color: var(--text-secondary);
    line-height: 1.8;
    font-size: 1.05rem;

    p {
      margin-bottom: 16px;
    }

    ul {
      padding-left: 20px;
      margin-bottom: 20px;
    }

    li {
      margin-bottom: 10px;
      position: relative;
      color: var(--text-secondary);

      &::marker {
        color: var(--primary-color);
      }
    }
  }
`;

const TechStackSection = styled.div`
  .tech-grid {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    margin-top: 16px;
  }

  .tech-badge {
    padding: 10px 20px;
    background: rgba(var(--primary-color-rgb), 0.1);
    border: 1px solid rgba(var(--primary-color-rgb), 0.2);
    border-radius: 12px;
    color: var(--text-primary);
    font-size: 0.95rem;
    font-weight: 500;
    transition: all 0.3s;
    display: flex;
    align-items: center;
    gap: 8px;

    &:hover {
      background: rgba(var(--primary-color-rgb), 0.2);
      transform: translateY(-2px);
      border-color: var(--primary-color);
    }
  }
`;

const ProjectDetailsAccordion = styled(Accordion)`
  .accordion-item {
    background: transparent;
    border: 1px solid var(--card-border);
    margin-bottom: 12px;
    border-radius: 12px !important;
    overflow: hidden;

    &:first-of-type {
      border-radius: 12px !important;
    }

    &:last-of-type {
      border-radius: 12px !important;
    }
  }

  .accordion-header {
    button {
      background: rgba(255, 255, 255, 0.05);
      color: var(--text-primary);
      font-weight: 600;
      font-size: 1.1rem;
      padding: 20px 24px;
      border: none;
      box-shadow: none;

      &:not(.collapsed) {
        background: rgba(var(--primary-color-rgb), 0.1);
        color: var(--primary-color);
      }

      &::after {
        filter: brightness(0.8);
      }
    }
  }

  .accordion-body {
    background: rgba(0, 0, 0, 0.2);
    color: var(--text-secondary);
    line-height: 1.7;
    padding: 24px;

    ul {
      padding-left: 20px;
      margin: 0;
    }

    li {
      margin-bottom: 12px;
      position: relative;
      padding-left: 8px;
      text-align: start;
      &::before {
        content: '▸';
        color: var(--primary-color);
        position: absolute;
        left: -12px;
      }
    }
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  margin-top: 32px;

  .btn {
    padding: 14px 28px;
    border-radius: 12px;
    font-weight: 600;
    font-size: 1rem;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    transition: all 0.3s;
    min-width: 160px;

    @media (max-width: 576px) {
      min-width: 140px;
      padding: 12px 20px;
    }
  }

  .btn-primary {
    background: linear-gradient(135deg, var(--primary-color), var(--secondary-color));
    border: none;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 20px rgba(var(--primary-color-rgb), 0.3);
    }
  }

  .btn-outline {
    background: transparent;
    border: 2px solid var(--primary-color);
    color: var(--primary-color);

    &:hover {
      background: rgba(var(--primary-color-rgb), 0.1);
      transform: translateY(-2px);
    }
  }

  .btn-secondary {
    background: rgba(255, 255, 255, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.2);
    color: var(--text-primary);

    &:hover {
      background: rgba(255, 255, 255, 0.15);
      border-color: var(--primary-color);
    }
  }
`;

const SidebarCard = styled.div`
  background: var(--card-bg);
  border: 1px solid var(--card-border);
  border-radius: 16px;
  padding: 28px;
  margin-bottom: 24px;
  backdrop-filter: blur(10px);

  .card-title {
    font-size: 1.2rem;
    font-weight: 700;
    color: var(--text-primary);
    margin-bottom: 20px;
    display: flex;
    align-items: center;
    gap: 10px;

    svg {
      color: var(--primary-color);
    }
  }
`;

const TimelineItem = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 20px;

  &:last-child {
    margin-bottom: 0;
  }

  .timeline-dot {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: linear-gradient(135deg, var(--primary-color), var(--secondary-color));
    flex-shrink: 0;
    position: relative;

    &::after {
      content: '';
      position: absolute;
      width: 2px;
      height: calc(100% + 20px);
      background: linear-gradient(to bottom, var(--primary-color), transparent);
      top: 24px;
      left: 50%;
      transform: translateX(-50%);
    }

    &:last-child::after {
      display: none;
    }
  }

  .timeline-content {
    flex: 1;
    padding-top: 2px;

    h4 {
      font-size: 1rem;
      font-weight: 600;
      color: var(--text-primary);
      margin-bottom: 4px;
    }

    p {
      font-size: 0.9rem;
      color: var(--text-secondary);
      margin: 0;
    }

    .timeline-date {
      font-size: 0.85rem;
      color: var(--muted);
      margin-top: 4px;
    }
  }
`;

const CTASection = styled.div`
  background: linear-gradient(135deg, 
    rgba(var(--primary-color-rgb), 0.15), 
    rgba(var(--secondary-color-rgb), 0.1)
  );
  border: 1px solid var(--card-border);
  border-radius: 16px;
  padding: 32px;
  text-align: center;
  margin-top: 40px;

  .cta-title {
    font-size: 1.5rem;
    font-weight: 700;
    color: var(--text-primary);
    margin-bottom: 12px;
  }

  .cta-text {
    color: var(--text-secondary);
    font-size: 1.05rem;
    line-height: 1.6;
    margin-bottom: 24px;
  }
`;

function ProjectDetails() {
  const { state: project } = useLocation();
  const navigate = useNavigate();
  const [activeImage, setActiveImage] = useState(0);

//   if (!project) {
//     return (
//       <PageWrapper>
//         <Content>
//           <div className="text-center py-5">
//             <h2>Project not found</h2>
//             <Button 
//               variant="outline" 
//               className="mt-3"
//               onClick={() => navigate('/projects')}
//             >
//               <BsArrowLeft /> Back to Projects
//             </Button>
//           </div>
//         </Content>
//       </PageWrapper>
//     );
//   }

const images = project?.screenshots || [];

  return (
    <PageWrapper>
      <Content>
        <MainContent>
          {/* Header with back button */}
          <ProjectHeader>
            <button className="back-btn" onClick={() => navigate(-1)}>
              <BsArrowLeft /> Back to Projects
            </button>
            <h1 className="title">{project?.title}</h1>
            {/* <div className="subtitle">{project?.subtitle || project?.role}</div> */}
            
            <MetaInfo>
              {project?.duration && (
                <div className="meta-item">
                  <BsCalendar />
                  <span>{project?.duration}</span>
                </div>
              )}
              {project?.role && (
                <div className="meta-item">
                  <BsPersonWorkspace />
                  <span>{project?.role}</span>
                </div>
              )}
              {project?.category && (
                <div className="meta-item">
                  <FiChevronRight />
                  <span>{project?.category}</span>
                </div>
              )}
            </MetaInfo>
          </ProjectHeader>

          {/* Image Gallery */}
          {images.length > 0 && (
            <ImageGallery>
              <img
                src={images[activeImage]}
                alt={`${project.title} screenshot ${activeImage + 1}`}
                className="main-image"
              />
              {images.length > 1 && (
                <div className="thumbnails">
                  {images.map((src, index) => (
                    <img
                      key={index}
                      src={src}
                      alt={`Thumbnail ${index + 1}`}
                      className={`thumbnail ${index === activeImage ? 'active' : ''}`}
                      onClick={() => setActiveImage(index)}
                    />
                  ))}
                </div>
              )}
            </ImageGallery>
          )}

          {/* Problem Section */}
          {project?.problem && (
            <ContentSection>
              <h3 className="section-title">The Challenge</h3>
              <div className="section-content">
                <p>{project?.problem}</p>
              </div>
            </ContentSection>
          )}

          {/* Solution Section */}
          {project?.solution && (
            <ContentSection>
              <h3 className="section-title">The Solution</h3>
              <div className="section-content">
                <p>{project?.solution}</p>
              </div>
            </ContentSection>
          )}

          {/* Key Features Accordion */}
          {project?.features && (
            <ProjectDetailsAccordion defaultActiveKey="0" alwaysOpen>
              <Accordion.Item eventKey="0">
                <Accordion.Header>✨ Key Features</Accordion.Header>
                <Accordion.Body>
                  <ul>
                    {project?.features.map((feature, index) => (
                      <li key={index}>{feature}</li>
                    ))}
                  </ul>
                </Accordion.Body>
              </Accordion.Item>
            </ProjectDetailsAccordion>
          )}

          {/* Tech Stack */}
          {project?.tech_stack && (
            <ContentSection>
              <h3 className="section-title">Technology Stack</h3>
              <TechStackSection>
                <div className="tech-grid">
                  {project?.tech_stack.map((tech, index) => (
                    <span key={index} className="tech-badge">
                      {tech}
                    </span>
                  ))}
                </div>
              </TechStackSection>
            </ContentSection>
          )}

          {/* CTA Section */}
          {project?.cta && (
            <CTASection>
              <h3 className="cta-title">Ready to see it in action?</h3>
              <p className="cta-text">{project?.cta}</p>
              <ActionButtons style={{ justifyContent: 'center' }}>
                {project?.live_url && (
                
                  
                  <LivePreviewButton
                  className="btn-primary"
                    url={project?.live_url}
                          innerText="Quick Preview"
                          icon= {MdOpenInNew}
                           style={{ backgrouund: 'linear-gradient(135deg, var(--primary-color), var(--secondary-color))', color: '#fff', width: 'fit-content', padding: '10px 28px', borderRadius: '12px',}}
    />

                )}
              </ActionButtons>
            </CTASection>
          )}
        </MainContent>

        {/* Sidebar */}
        <Sidebar>
          {/* Project Timeline */}
          {(project?.timeline || project?.duration) && (
            <SidebarCard>
              <h4 className="card-title">
                <BsCalendar /> Project Timeline
              </h4>
              <TimelineItem>
                <div className="timeline-dot" />
                <div className="timeline-content">
                  <h4>Project Start</h4>
                  <p>Initial planning and research</p>
                  <div className="timeline-date">
                    {project.timeline?.start || 'Q1 2023'}
                  </div>
                </div>
              </TimelineItem>
              <TimelineItem>
                <div className="timeline-dot" />
                <div className="timeline-content">
                  <h4>Development Phase</h4>
                  <p>Core implementation and testing</p>
                  <div className="timeline-date">
                    {project.timeline?.development || 'Q2 2023'}
                  </div>
                </div>
              </TimelineItem>
              <TimelineItem>
                <div className="timeline-dot" />
                <div className="timeline-content">
                  <h4>Launch</h4>
                  <p>Deployment and release</p>
                  <div className="timeline-date">
                    {project.timeline?.launch || 'Q3 2023'}
                  </div>
                </div>
              </TimelineItem>
            </SidebarCard>
          )}

          {/* Quick Stats */}
          {project?.stats && (
            <SidebarCard>
              <h4 className="card-title">Project Stats</h4>
              {Object.entries(project?.stats).map(([key, value], index) => (
                <div key={index} style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between',
                  marginBottom: '12px',
                  paddingBottom: '12px',
                  borderBottom: index < Object.keys(project?.stats).length - 1 ? '1px solid rgba(255,255,255,0.1)' : 'none'
                }}>
                  <span style={{ color: 'var(--text-secondary)' }}>{key}:</span>
                  <span style={{ 
                    color: 'var(--text-primary)', 
                    fontWeight: '600' 
                  }}>
                    {value}
                  </span>
                </div>
              ))}
            </SidebarCard>
          )}

          {/* Implementation Details */}
          {project?.implementation && (
            <SidebarCard>
              <h4 className="card-title">Implementation Highlights</h4>
              <ul style={{ 
                paddingLeft: '20px',
                color: 'var(--text-secondary)',
                lineHeight: '1.7'
              }}>
                {project?.implementation.slice(0, 4).map((item, index) => (
                  <li key={index} style={{ marginBottom: '10px', textAlign:"start" }}>{item}</li>
                ))}
              </ul>
            </SidebarCard>
          )}

          {/* Action Buttons in Sidebar */}
          <SidebarCard>
            <h4 className="card-title">Project Links</h4>
            <ActionButtons style={{ flexDirection: 'column' }}>
              {project?.github_url && (
                <Button 
                  variant="outline" 
                  href={project?.github_url} 
                  target="_blank"
                  className="btn-outline"
                >
                  <BsGithub /> View Source Code
                </Button>
              )}
              {project?.live_url && (
                <Button 
                  variant="primary" 
                  href={project?.live_url} 
                  target="_blank"
                  className="btn-primary"
                >
                  <CgWebsite /> Visit Live Site
                </Button>
              )}
              <Button 
                variant="secondary" 
                onClick={() => navigate(-1)}
                className="btn-secondary"
              >
                <BsArrowLeft /> Back to Projects
              </Button>
            </ActionButtons>
          </SidebarCard>
        </Sidebar>
      </Content>
    </PageWrapper>
  );
}

export default ProjectDetails;